/**
 * /market/connectors/:short_id — user-mode connector catalog detail.
 *
 * Push #43: forked from the previously-shared shape — admin mode now
 * renders AdminMarketDetail at /admin/market/connectors/:short_id. This
 * file is the USER-MODE consumer only; it always navigates within /market
 * and /connectors paths and never crosses into /admin/*.
 *
 * Push 4-1d-m (2026-07-06): URL moved from /market/:short_id to
 * /market/connectors/:short_id per market route kind split. Component
 * body unchanged — still renders connector-template detail. Model
 * templates live at /market/models/:short_id (ModelMarketDetail.tsx).
 *
 * Mirrors the Lightning record-page shape used on /connectors/:slug
 * (Connector Detail) so every "view a connector" surface looks the
 * same — only the action bar + sections differ:
 *   - Action bar is contextual (Open / Request access / Coming soon) based
 *     on user-role install state.
 *   - Sections: Overview + Tools (no Activity tab — this is the template,
 *     not an instance, so there's no dispatch history to surface).
 */

import { Button } from '@olytic/foundation';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useSession } from '../hooks/useSession.tsx';
import {
  fetchCatalogConnectorDetail,
  requestConnectorAccess,
  ApiError,
  type CatalogDetail,
  type CatalogToolEntry,
} from '../lib/api.ts';
import { ConnectorLogo } from '../components/ConnectorLogo.tsx';
import { ObjectIcon } from '../components/ObjectIcon.tsx';
import { ModeBadge, SlaDisclosure } from '../components/ModeBadge.tsx';
// Push 5-F (2026-07-11): record-detail full-page gate now renders SkeletonDetail
// (page-shape hint) instead of the branded 3-arc spinner. Matches
// AdminConnectorDetail / AdminModelDetail / AdminPromptDetail pattern.
import { SkeletonDetail } from '../components/Skeleton.tsx';
import { Pill } from '../components/StatusPill.tsx';
import { useToast } from '../components/Toast.tsx';
import { LightningTabs, type LightningTab } from '../components/LightningTabs.tsx';
import { RecordActions } from '../components/RecordActions.tsx';
import { RecordDetailLayout } from '../components/RecordDetailLayout.tsx';

export function LibraryDetail() {
  // Phase 2 Block 2A (2026-06-30): URL param is the connector template's
  // 15-char short_id (cnt prefix). Market detail pages discriminate by
  // short_id; the market→instance bug at navigate('/connectors/...') now
  // routes to the instance's own short_id (cni prefix).
  const { short_id = '' } = useParams<{ short_id: string }>();
  const { user } = useSession();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [detail, setDetail] = useState<CatalogDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [requesting, setRequesting] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('overview');

  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    setLoadError(null);
    fetchCatalogConnectorDetail(short_id)
      .then((d) => {
        if (!cancelled) setDetail(d);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoadError(err instanceof ApiError ? err.message : 'Could not load connector.');
      });
    return () => {
      cancelled = true;
    };
  }, [short_id]);

  if (user === null) return null;

  if (loadError !== null) {
    return (
      <div>
        <div className="lightning-highlight">
          <div className="lightning-highlight-left">
            <ObjectIcon kind="catalog" size="lg" />
            <div className="lightning-highlight-text">
              <h1 className="lightning-title">Not found</h1>
              <p
                className="muted"
                style={{ margin: '6px 0 0', fontSize: '13px', maxWidth: '72ch', lineHeight: 1.45 }}
              >
                {loadError}
              </p>
            </div>
          </div>
          <RecordActions actions={[]} />
        </div>
      </div>
    );
  }

  if (detail === null) return <SkeletonDetail />;

  const { template, tools } = detail;
  // canInstall surfaces "Install" / "Manage assignments" affordances. In
  // user-mode this is a rare edge case (admin/owner viewing user-mode
  // /market/:slug via the gear-flip) — the proper admin flow goes through
  // /admin/market/:slug → AdminMarketDetail. We keep the affordance here so
  // admins who happen to land on the user surface still have a path forward,
  // but it routes back into /admin/* destinations.
  const canInstall = user.role === 'owner' || user.role === 'admin';
  const scopes = readScopes(template.oauth_config);

  async function handleRequestAccess() {
    setRequesting(true);
    try {
      const res = await requestConnectorAccess(template.name);
      showToast({
        kind: 'success',
        message: res.already_requested
          ? `You've already requested ${template.display_name}. Your admin will see it in their inbox.`
          : `Requested ${template.display_name}. Your admin will see it in their inbox.`,
      });
    } catch (err) {
      showToast({
        kind: 'error',
        message: err instanceof ApiError ? err.message : 'Could not submit request.',
      });
    } finally {
      setRequesting(false);
    }
  }

  return (
    <div>
      {/* Phase 2 Block 2B EXISTS-MIGRATE (2026-06-30) — wraps hand-rolled
         hero/main/rail in the canonical RecordDetailLayout primitive. */}
      <RecordDetailLayout
        hero={
          <div className="lightning-highlight">
            <div className="lightning-highlight-left">
              <ConnectorLogo
                slug={template.name}
                name={template.display_name}
                logoUrl={template.logo_url}
                size="lg"
              />
              <div className="lightning-highlight-text">
                {/* Push 4-1ao (2026-07-11): .lightning-breadcrumb arrow-link
                    deleted per universal record-detail back-nav scrub. */}
                <h1 className="lightning-title">{template.display_name}</h1>
                <div className="lightning-meta">
                  <ModeBadge
                    mode={template.connector_mode}
                    vendorName={template.display_name}
                  />
                  {template.is_preview ? (
                    <Pill kind="neutral">Preview</Pill>
                  ) : template.is_mine ? (
                    <Pill kind="success" dot>Assigned to you</Pill>
                  ) : template.is_installed ? (
                    <Pill kind="info">Installed</Pill>
                  ) : null}
                  <span className="muted">
                    {tools.length} thing{tools.length === 1 ? '' : 's'} it can do
                  </span>
                </div>
                <SlaDisclosure text={template.sla_disclosure} />
              </div>
            </div>
        {/* Push #36. PrimaryAction picks one of five stateful CTAs based
           on tenant install state; it doesn't fit the flat descriptor shape,
           so it goes through the `extra` escape hatch. Back-nav handled by
           RecordActions. If a 4th action ever gets added here, it must go
           into `actions[]` so the overflow rule applies.
           Push #43. user-mode only. Admins use AdminMarketDetail at
           /admin/market/:slug. The Install/Assign CTAs that route into
           /admin/* are present here for edge-case role=admin landings via
           the gear-flip but are not the primary admin path. */}
        <RecordActions
          actions={[]}
          extra={
            <PrimaryAction
              tpl={template}
              canInstall={canInstall}
              requesting={requesting}
              onRequest={handleRequestAccess}
              /* Phase 2 Block 2A: the market→instance bug fix. Was navigating
                 to /connectors/${template.name} which lands on the FIRST
                 instance sharing that template slug; now routes to the
                 instance's own cni-prefixed short_id via template.instance_short_id
                 (the API response's "my installed instance" pointer). When the
                 caller hasn't installed an instance yet, the button shape is
                 different (Install/Request, not Open). */
              onOpen={() => template.instance_short_id !== null && navigate(`/connectors/${template.instance_short_id}`)}
              onInstall={() => navigate('/admin/connectors')}
              onAssign={() => navigate('/admin/assignments')}
            />
          }
        />
          </div>
        }
        main={
          <div className="record-surface record-surface--tabs-only">
        {(() => {
          const tabs: LightningTab[] = [
            {
              id: 'overview',
              label: 'Overview',
              render: () => (
                <div className="lightning-section">
                  <p className="prose-emphasis" style={{ margin: 0 }}>
                    {template.description ??
                      `${template.display_name} brings its tools into your AI assistant.`}
                  </p>
                </div>
              ),
            },
            {
              id: 'tools',
              label: 'Tools',
              count: tools.length,
              render: () => (
                <div className="lightning-section">
                  <p className="muted" style={{ fontSize: '12px', marginBottom: '12px' }}>
                    These are the actions {template.display_name} brings to your AI assistant.
                  </p>
                  <ToolsSection
                    tools={tools}
                    connectorName={template.display_name}
                  />
                </div>
              ),
            },
            ...(scopes.length > 0
              ? [
                  {
                    id: 'permissions',
                    label: 'Permissions',
                    count: scopes.length,
                    render: () => (
                      <div className="lightning-section">
                        <p
                          className="muted"
                          style={{ marginTop: 0, marginBottom: '12px', fontSize: '13px' }}
                        >
                          When you sign in to <strong>{template.display_name}</strong>,
                          you'll be asked to allow these. Olytic only uses what's
                          listed below.
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          {scopes.map((s) => (
                            <code
                              key={s}
                              style={{
                                fontFamily: 'var(--font-mono)',
                                fontSize: '12px',
                                padding: '6px 10px',
                                background: 'var(--surface-bar)',
                                border: '1px solid var(--border)',
                                borderRadius: '6px',
                                color: 'var(--olytic-obsidian)',
                                wordBreak: 'break-all',
                              }}
                            >
                              {s}
                            </code>
                          ))}
                        </div>
                      </div>
                    ),
                  } as LightningTab,
                ]
              : []),
          ];
          return <LightningTabs tabs={tabs} value={activeTab} onChange={setActiveTab} />;
        })()}
          </div>
        }
        rail={
          <>
            {/* Side rail. catalog snapshot derived from template data.
                "How it works" moved to top per Ean 2026-06-04: leads with the
                value-prop, then snapshot, then who-can-install action context. */}
            <div className="rail-card">
              <p className="rail-card-label">How it works</p>
              <p className="rail-card-body">
                Your AI assistant calls {template.display_name} through Olytic so your sign-in stays safely on our side, working with Claude, ChatGPT, Cursor, and other MCP-aware clients.
              </p>
        </div>

        <div className="rail-card">
          <p className="rail-card-label">At a glance</p>
          <div className="rail-row">
            <span className="rail-row-label">Tools</span>
            <span className="rail-row-value">{tools.length}</span>
          </div>
          <div className="rail-row">
            <span className="rail-row-label">Connection</span>
            <span className="rail-row-value" style={{ fontSize: '11px' }}>
              <ModeBadge mode={template.connector_mode} vendorName={template.display_name} />
            </span>
          </div>
        </div>

            <div className="rail-card">
              <p className="rail-card-label">Who can install</p>
              <p className="rail-card-body">
                {template.is_preview
                  ? 'This connector is in preview and not yet available.'
                  : canInstall
                    ? "You're a workspace admin. Install it from here, then assign it to teammates."
                    : 'Ask a workspace admin to install this and assign it to you.'}
              </p>
            </div>
          </>
        }
      />
    </div>
  );
}

/**
 * Friendly group labels for D-093 access_class — same shape used on
 * /connectors/:slug ToolsSection so the two pages share the visual treatment.
 */
const ACCESS_CLASS_GROUPS: ReadonlyArray<{
  key: 'read' | 'write' | 'destructive';
  label: string;
  body: string;
}> = [
  {
    key: 'read',
    label: 'Things it can read',
    body: 'Safe to leave on. Your assistant can look up information but cannot change anything.',
  },
  {
    key: 'write',
    label: 'Things it can create or change',
    body: 'Your assistant can draft, edit, send, or move items. Reversible in most cases.',
  },
  {
    key: 'destructive',
    label: 'Things it can remove',
    body: 'Harder to undo. Your assistant can delete or trash items here.',
  },
];

function ToolsSection({
  tools,
  connectorName,
}: {
  tools: CatalogToolEntry[];
  connectorName: string;
}) {
  if (tools.length === 0) {
    return (
      <p className="muted" style={{ margin: 0 }}>
        Nothing here yet. The tool list for this connector hasn't been added.
      </p>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {ACCESS_CLASS_GROUPS.map((group) => {
        const groupTools = tools.filter(
          (t) => (t.access_class ?? 'read') === group.key,
        );
        if (groupTools.length === 0) return null;
        return (
          <section key={group.key}>
            <div style={{ marginBottom: '10px' }}>
              <h4
                style={{
                  margin: '0 0 2px',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: 'var(--olytic-obsidian)',
                  letterSpacing: '0.01em',
                }}
              >
                {group.label}
                <span
                  className="muted"
                  style={{ marginLeft: '8px', fontWeight: 500 }}
                >
                  {groupTools.length}
                </span>
              </h4>
              <p
                className="muted"
                style={{ margin: 0, fontSize: '12px', lineHeight: 1.4 }}
              >
                {group.body}
              </p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {groupTools.map((t) => (
                <div key={t.id} className="card card-padded">
                  <h4
                    style={{
                      margin: '0 0 4px',
                      fontSize: '14px',
                      fontWeight: 600,
                      color: 'var(--olytic-obsidian)',
                    }}
                  >
                    {t.display_name}
                  </h4>
                  {t.when_to_use !== null && t.when_to_use.trim() !== '' && (
                    <p
                      style={{
                        margin: 0,
                        fontSize: '13px',
                        color: 'var(--fg-muted)',
                        lineHeight: 1.45,
                      }}
                    >
                      {t.when_to_use}
                    </p>
                  )}
                </div>
              ))}
            </div>
            {/* Suppress unused-var lint when connectorName isn't referenced
                directly. the group header copy speaks for itself. */}
            {connectorName.length === -1 && <span />}
          </section>
        );
      })}
    </div>
  );
}

function PrimaryAction({
  tpl,
  canInstall,
  requesting,
  onRequest,
  onOpen,
  onInstall,
  onAssign,
}: {
  tpl: CatalogDetail['template'];
  canInstall: boolean;
  requesting: boolean;
  onRequest: () => void;
  onOpen: () => void;
  onInstall: () => void;
  onAssign: () => void;
}) {
  if (tpl.is_preview) {
    return (
      <Button type="button" variant="outline" disabled>
        In preview
      </Button>
    );
  }
  if (tpl.is_mine) {
    // Push 64 (Ean nitpick): match the model market detail's "Open your
    // instance" CTA so the two market surfaces read consistently. Both
    // navigate the user from the catalog card to their installed
    // instance detail page.
    return (
      <Button type="button" variant="brand" onClick={onOpen}>
        Open your instance
      </Button>
    );
  }
  if (tpl.is_installed && canInstall) {
    return (
      <Button type="button" variant="brand" onClick={onAssign}>
        Manage assignments
      </Button>
    );
  }
  if (!tpl.is_installed && canInstall) {
    return (
      <Button type="button" variant="brand" onClick={onInstall}>
        Install for workspace
      </Button>
    );
  }
  return (
    <Button type="button" variant="brand" onClick={onRequest} disabled={requesting}>
      {requesting ? 'Sending…' : 'Request access'}
    </Button>
  );
}

function readScopes(oauthConfig: unknown): string[] {
  if (oauthConfig === null || typeof oauthConfig !== 'object') return [];
  const scopes = (oauthConfig as { scopes?: unknown }).scopes;
  if (!Array.isArray(scopes)) return [];
  return scopes.filter((s): s is string => typeof s === 'string');
}
